import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import BackMethods from "../../back_component/BackMethods";
import {
    FaPlus, FaEdit, FaTrash, FaMapMarkerAlt,
    FaCheckCircle, FaTimesCircle, FaArrowRight,
    FaSearch, FaFilter, FaCity, FaMapSigns, FaGlobe,
    FaChevronLeft, FaChevronRight
} from "react-icons/fa";

export default function LocationsManagementPage() {
    const { backEnd_request, getUser } = BackMethods();
    const navigate = useNavigate();

    // حالات البيانات والتحميل
    const [loading, setLoading] = useState(false);
    const [locations, setLocations] = useState([]);

    // --- حالات الترقيم الصفحي (Pagination States) ---
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalItems, setTotalItems] = useState(0);

    // حالات التصفية والبحث
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    // حالات المودال
    const [showModal, setShowModal] = useState(false);
    const [currentLocation, setCurrentLocation] = useState({
        governorate: getUser()?.governorate || "",
        city: "",
        sector: "",
        isActive: true
    });
    const [isEdit, setIsEdit] = useState(false);

    // دالة جلب البيانات من السيرفر (Server-side Pagination)
    const loadLocations = useCallback(async () => {
        setLoading(true);
        try {
            // بناء المعاملات بناءً على ما يقبله الـ API الخاص بك
            const params = {
                page: currentPage,
                size: pageSize,
                includeInactive: statusFilter === "all" || statusFilter === "inactive"
            };

            const response = await backEnd_request.get("/Locations", { params });

            // تحديث البيانات بناءً على هيكلة JSON المرفقة (total, data)
            setLocations(response.data.data || []);
            setTotalItems(response.data.total || 0);
        } catch (err) {
            console.error("خطأ في جلب المواقع", err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, statusFilter, backEnd_request]);

    useEffect(() => {
        loadLocations();
    }, [loadLocations]);

    // حساب إجمالي الصفحات
    const totalPages = Math.ceil(totalItems / pageSize);

    // منطق البحث (يتم في نتائج الصفحة الحالية لضمان السرعة)
    const filteredLocations = locations.filter(item => {
        const term = searchTerm.toLowerCase();
        return (
            item.governorate?.toLowerCase().includes(term) ||
            item.city?.toLowerCase().includes(term) ||
            item.sector?.toLowerCase().includes(term)
        );
    });

    const handleDelete = async (id) => {
        if (window.confirm("هل أنت متأكد من حذف هذا الموقع؟")) {
            try {
                await backEnd_request.delete(`/Locations/${id}`);
                // إعادة التحميل لضمان تحديث الترقيم والعدد الإجمالي
                loadLocations();
            } catch (err) {
                alert("حدث خطأ أثناء الحذف");
            }
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (isEdit) {
                await backEnd_request.post(`/Locations/${currentLocation.locationId}`, currentLocation);
            } else {
                await backEnd_request.post("/Locations", currentLocation);
            }
            setShowModal(false);
            loadLocations();
            setCurrentLocation({ governorate: getUser()?.governorate || "", city: "", sector: "", isActive: true });
        } catch (err) {
            alert("خطأ في عملية الحفظ");
        }
    };

    return (
        <div className="bg-light min-vh-100 py-4" dir="rtl">
            <div className="container shadow-sm bg-white rounded-4 p-4">

                {/* الرأس - Header */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 pb-3 border-bottom gap-3">
                    <div>
                        <h3 className="fw-bold text-primary mb-1">
                            <FaMapMarkerAlt className="ms-2" /> إدارة المواقع
                        </h3>
                        <p className="text-muted small mb-0">إجمالي النتائج: {totalItems}</p>
                    </div>
                    <div className="d-flex gap-2">
                        <button
                            onClick={() => {
                                setIsEdit(false);
                                setCurrentLocation({ governorate: getUser()?.governorate || "", city: "", sector: "", isActive: true });
                                setShowModal(true);
                            }}
                            className="btn btn-primary d-flex align-items-center px-4 rounded-pill shadow-sm"
                        >
                            <FaPlus className="ms-2" /> إضافة موقع جديد
                        </button>
                        <button onClick={() => navigate(-1)} className="btn btn-outline-secondary rounded-pill">
                            <FaArrowRight className="ms-1" /> رجوع
                        </button>
                    </div>
                </div>

                {/* شريط الفلترة والبحث */}
                <div className="row g-3 mb-4 bg-light p-3 rounded-3 border">
                    <div className="col-md-5">
                        <div className="input-group border rounded-pill bg-white px-3 py-1">
                            <span className="input-group-text bg-transparent border-0 text-muted"><FaSearch /></span>
                            <input
                                type="text"
                                className="form-control border-0 bg-transparent shadow-none"
                                placeholder="ابحث في هذه الصفحة..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="input-group border rounded-pill bg-white px-3 py-1">
                            <span className="input-group-text bg-transparent border-0 text-muted"><FaFilter className="ms-1" /> الحالة:</span>
                            <select
                                className="form-select border-0 bg-transparent shadow-none"
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                            >
                                <option value="all">الكل</option>
                                <option value="active">نشط فقط</option>
                                <option value="inactive">متوقف فقط</option>
                            </select>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <select
                            className="form-select border rounded-pill bg-white px-3 py-2"
                            value={pageSize}
                            onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                        >
                            <option value={10}>10 سجلات / صفحة</option>
                            <option value={50}>50 سجل / صفحة</option>
                            <option value={100}>100 سجل / صفحة</option>
                        </select>
                    </div>
                </div>

                {/* عرض الجدول */}
                {loading ? (
                    <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
                ) : (
                    <>
                        <div className="table-responsive">
                            <table className="table table-hover align-middle border-light">
                                <thead className="table-light">
                                    <tr>
                                        <th className="py-3 border-0">المحافظة</th>
                                        <th className="py-3 border-0">المدينة</th>
                                        <th className="py-3 border-0">المنطقة / الحي</th>
                                        <th className="py-3 border-0 text-center">الحالة</th>
                                        <th className="py-3 border-0 text-center">إجراءات</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredLocations.map((item) => (
                                        <tr key={item.locationId}>
                                            <td className="fw-bold text-dark py-3">
                                                <FaGlobe className="text-secondary ms-2" />{item.governorate}
                                            </td>
                                            <td><FaCity className="text-muted ms-1" /> {item.city}</td>
                                            <td><FaMapSigns className="text-muted ms-1" /> {item.sector}</td>
                                            <td className="text-center">
                                                {item.isActive ? (
                                                    <span className="badge bg-success-subtle text-success border border-success-subtle px-3 py-2 rounded-pill fw-normal">
                                                        <FaCheckCircle className="ms-1" /> نشط
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-3 py-2 rounded-pill fw-normal">
                                                        <FaTimesCircle className="ms-1" /> متوقف
                                                    </span>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <div className="btn-group shadow-sm bg-white rounded-3">
                                                    <button
                                                        onClick={() => { setCurrentLocation(item); setIsEdit(true); setShowModal(true); }}
                                                        className="btn btn-outline-primary btn-sm border-0 px-3"
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(item.locationId)}
                                                        className="btn btn-outline-danger btn-sm border-0 px-3"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* --- أزرار الترقيم الصفحي (Pagination Controls) --- */}
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 border-top pt-3 gap-3">
                            <div className="text-muted small">
                                عرض صفحة <b>{currentPage}</b> من <b>{totalPages}</b> (إجمالي السجلات: {totalItems})
                            </div>
                            <nav>
                                <ul className="pagination mb-0 gap-1">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button className="page-link rounded-circle border-0 shadow-sm" onClick={() => setCurrentPage(prev => prev - 1)}>
                                            <FaChevronRight size={12} />
                                        </button>
                                    </li>

                                    {[...Array(totalPages)].map((_, index) => {
                                        const pageNum = index + 1;
                                        // منطق إظهار أرقام محددة فقط لعدم ازدحام الواجهة
                                        if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
                                            return (
                                                <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                    <button className="page-link rounded-pill border-0 mx-1 shadow-sm" onClick={() => setCurrentPage(pageNum)}>
                                                        {pageNum}
                                                    </button>
                                                </li>
                                            );
                                        } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                            return <li key={pageNum} className="px-1 align-self-end">...</li>;
                                        }
                                        return null;
                                    })}

                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button className="page-link rounded-circle border-0 shadow-sm" onClick={() => setCurrentPage(prev => prev + 1)}>
                                            <FaChevronLeft size={12} />
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </>
                )}
            </div>

            {/* Modal - إضافة وتعديل الموقع */}
            {showModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg rounded-4">
                            <div className="modal-header border-0 pt-4 px-4">
                                <h5 className="fw-bold">{isEdit ? "تعديل بيانات الموقع" : "إضافة موقع جديد"}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <form onSubmit={handleSave}>
                                <div className="modal-body p-4">
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">المحافظة</label>
                                        <input
                                            type="text" className="form-control rounded-3 bg-light"
                                            value={currentLocation.governorate}
                                            onChange={(e) => setCurrentLocation({ ...currentLocation, governorate: e.target.value })}
                                            required disabled
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">المدينة</label>
                                        <input
                                            type="text" className="form-control rounded-3"
                                            placeholder="مثال: إدلب"
                                            value={currentLocation.city}
                                            onChange={(e) => setCurrentLocation({ ...currentLocation, city: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">المنطقة / الحي (Sector)</label>
                                        <input
                                            type="text" className="form-control rounded-3"
                                            placeholder="مثال: شارع الثلاثين"
                                            value={currentLocation.sector}
                                            onChange={(e) => setCurrentLocation({ ...currentLocation, sector: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="form-check form-switch mt-3">
                                        <input
                                            className="form-check-input" type="checkbox"
                                            checked={currentLocation.isActive}
                                            onChange={(e) => setCurrentLocation({ ...currentLocation, isActive: e.target.checked })}
                                        />
                                        <label className="form-check-label fw-bold small">تفعيل هذا الموقع في النظام</label>
                                    </div>
                                </div>
                                <div className="modal-footer border-0 p-4 pt-0">
                                    <button type="button" className="btn btn-light rounded-3 px-4" onClick={() => setShowModal(false)}>إغلاق</button>
                                    <button type="submit" className="btn btn-primary rounded-3 px-4">حفظ البيانات</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}